import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Check, X, ThumbsUp, Ban, CalendarDays } from 'lucide-react';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { Button } from '../components';
import { B2bAgenda } from '../components/B2bAgenda';
import { B2bAfspraakDetail, B2bAfspraakFormulier } from '../components/B2bAgendaPanelen';
import {
  annuleerB2bAfspraak,
  bevestigB2bAfspraak,
  fiatteerB2bAfspraak,
  getB2bAfspraken,
  planB2bAfspraak,
  weigerB2bAfspraak,
  wijsB2bAfspraakAf,
  type B2bAfspraak,
} from '../services/lopendeTrajectenApi';

/**
 * Trajecten → Agenda. Outlook-achtige kalender op b2b_appointments.
 * Wederzijdse handdruk (migratie 132): wat de opdrachtgever inplant moet de
 * professional accepteren; wat de professional inplant moet de opdrachtgever
 * bevestigen — anders bereiken de uren de maandfactuur niet.
 */
const QK = ['lopende-trajecten', 'b2b'];
const foutmelding = (e: any) => toast.error(e?.response?.data?.message || 'Er ging iets mis');

const EmployerTrajectenAgendaPage: React.FC = () => {
  useCockpitTitle('Agenda', 'Afspraken met professionals — wederzijds bevestigd, fraudebestendig gedeclareerd');
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: QK, queryFn: getB2bAfspraken });
  const [nieuwOp, setNieuwOp] = useState<Date | null>(null);
  const [geselecteerdId, setGeselecteerdId] = useState<string | null>(null);

  const ververs = () => {
    qc.invalidateQueries({ queryKey: QK });
    qc.invalidateQueries({ queryKey: ['lopende-trajecten', 'overzicht'] });
  };
  const actie = (fn: (id: string) => Promise<unknown>, melding: string) => ({
    mutationFn: fn,
    onSuccess: () => {
      toast.success(melding);
      ververs();
    },
    onError: foutmelding,
  });

  const plan = useMutation({
    mutationFn: planB2bAfspraak,
    onSuccess: () => {
      toast.success('Afspraak ingepland — wacht op acceptatie door de professional');
      setNieuwOp(null);
      ververs();
    },
    onError: foutmelding,
  });
  const bevestig = useMutation(actie(bevestigB2bAfspraak, 'Afspraak bevestigd'));
  const weiger = useMutation(actie((id: string) => weigerB2bAfspraak(id, window.prompt('Reden van weigering (optioneel, zichtbaar voor de professional):') || undefined), 'Afspraak geweigerd'));
  const annuleer = useMutation(actie(annuleerB2bAfspraak, 'Afspraak geannuleerd'));
  const fiat = useMutation(actie(fiatteerB2bAfspraak, 'Uren gefiatteerd — gaan mee in de maandfactuur'));
  const wijsAf = useMutation({
    mutationFn: ({ id, reden }: { id: string; reden: string }) => wijsB2bAfspraakAf(id, reden),
    onSuccess: () => {
      toast.success('Uren afgewezen');
      ververs();
    },
    onError: foutmelding,
  });

  const regels = data?.regels ?? [];
  const geselecteerd = regels.find((r) => r.id === geselecteerdId) ?? null;
  const teBevestigen = regels.filter((r) => r.status === 'GEPLAND' && r.bevestiging_status === 'WACHT' && r.voorgesteld_door === 'professional');
  const teFiatteren = regels.filter((r) => r.status === 'UITGEVOERD');

  const acties = (a: B2bAfspraak) => {
    if (a.status === 'GEPLAND' && a.bevestiging_status === 'WACHT' && a.voorgesteld_door === 'professional') {
      return (
        <>
          <Button size="sm" onClick={() => bevestig.mutate(a.id)} loading={bevestig.isPending}><Check className="w-4 h-4 mr-1" />Bevestigen</Button>
          <Button size="sm" variant="outline" onClick={() => weiger.mutate(a.id)} loading={weiger.isPending}><X className="w-4 h-4 mr-1" />Weigeren</Button>
        </>
      );
    }
    if (a.status === 'GEPLAND') {
      return <Button size="sm" variant="outline" onClick={() => window.confirm('Afspraak annuleren?') && annuleer.mutate(a.id)} loading={annuleer.isPending}><Ban className="w-4 h-4 mr-1" />Annuleren</Button>;
    }
    if (a.status === 'UITGEVOERD') {
      return (
        <>
          <Button size="sm" onClick={() => fiat.mutate(a.id)} loading={fiat.isPending}><ThumbsUp className="w-4 h-4 mr-1" />Uren fiatteren</Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const reden = window.prompt('Reden van afwijzing (de professional ziet deze):');
              if (reden && reden.trim().length >= 3) wijsAf.mutate({ id: a.id, reden });
            }}
          >
            Afwijzen
          </Button>
        </>
      );
    }
    return null;
  };

  return (
    <div className="px-4 sm:px-6 py-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2 text-sm">
          <span className={`inline-flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-full border ${teBevestigen.length ? 'bg-gold-50 border-gold-200 text-navy-900 font-medium' : 'bg-white border-gray-200 text-navy-700'}`}>
            <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full bg-gold-400 text-navy-900 text-xs font-bold">{teBevestigen.length}</span>
            te bevestigen
          </span>
          <span className={`inline-flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-full border ${teFiatteren.length ? 'bg-swirl-50 border-swirl-200 text-navy-900 font-medium' : 'bg-white border-gray-200 text-navy-700'}`}>
            <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full bg-swirl-400 text-navy-900 text-xs font-bold">{teFiatteren.length}</span>
            uren te fiatteren
          </span>
        </div>
        <Button onClick={() => { const d = new Date(); d.setMinutes(0, 0, 0); d.setHours(d.getHours() + 1); setNieuwOp(d); }}>
          <Plus className="w-4 h-4 mr-1" />Nieuwe afspraak
        </Button>
      </div>

      {teBevestigen.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
          <p className="text-sm font-medium text-amber-900">Door professionals ingepland — uw bevestiging is nodig, anders gaan de uren niet mee in de maandfactuur</p>
          {teBevestigen.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 bg-white rounded-md border border-amber-200 px-3 py-2 text-sm">
              <button type="button" className="text-left hover:underline" onClick={() => setGeselecteerdId(a.id)}>
                <span className="font-medium">{a.titel}</span> · {a.professional_naam} · {new Date(a.start_tijd).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' })} ({a.uren_gepland} u)
              </button>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => bevestig.mutate(a.id)}>Bevestigen</Button>
                <Button size="sm" variant="outline" onClick={() => weiger.mutate(a.id)}>Weigeren</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isLoading ? (
        <p className="text-gray-600 py-12 text-center">Laden…</p>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_300px] gap-4 items-start">
          <B2bAgenda
            afspraken={regels}
            ondertitel={(a) => a.professional_naam}
            onAfspraakClick={(a) => setGeselecteerdId(a.id)}
            onSlotClick={setNieuwOp}
            geselecteerdId={geselecteerdId}
          />
          {geselecteerd ? (
            <B2bAfspraakDetail afspraak={geselecteerd} tegenpartij={`${geselecteerd.professional_naam} (${geselecteerd.discipline_label})`} kijker="employer" onSluiten={() => setGeselecteerdId(null)}>
              {acties(geselecteerd)}
            </B2bAfspraakDetail>
          ) : (
            <div className="bg-navy-50/60 rounded-xl border border-navy-100 p-6 text-sm text-navy-700 leading-relaxed">
              <CalendarDays className="w-12 h-12 text-navy-900 mb-4 mx-auto" strokeWidth={1.5} />
              Klik op een afspraak voor details, of op een leeg tijdvak om een afspraak in te plannen.
            </div>
          )}
        </div>
      )}

      {nieuwOp && (
        <B2bAfspraakFormulier
          start={nieuwOp}
          tegenpartijLabel="Professional"
          tegenpartijen={(data?.pool ?? []).map((p) => ({ id: p.professional_id, label: `${p.naam} — ${p.discipline_label}${p.uurtarief != null ? ` (€ ${p.uurtarief.toFixed(2)}/u)` : ''}` }))}
          bevestigHint="De professional moet deze afspraak accepteren. Uren kunnen pas ná de afspraak worden ingediend en komen alleen na uw fiat op de maandfactuur."
          bezig={plan.isPending}
          onSluiten={() => setNieuwOp(null)}
          onOpslaan={({ tegenpartij_id, ...rest }) => plan.mutate({ professional_id: tegenpartij_id, ...rest })}
        />
      )}
    </div>
  );
};

export default EmployerTrajectenAgendaPage;
